const { debounce, getElement, getElements } = window.BAO.Utils

class CollectionFiltersRenderer {
  /**
   * @param {CollectionFiltersForm} form
   */
  constructor (form) {
    /**
     * @type {CollectionFiltersForm}
     * @public
     */
    this.form = form
  }

  /**
   * @param {Function} filterDataUrl
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  fromCache (filterDataUrl, e) {
    const html = this.form.filterData.find(element => filterDataUrl(element))
      .html

    this.form.log('fromCache', { filterDataUrl, e, html })

    this.render(html, e)
  }

  /**
   * @param {String} section
   * @param {String} url
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  async fromFetch (section, url, e) {
    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)

    this.form.log('fromFetch', { section, url, html })

    this.form.filterData.push({ html, url })

    this.render(html, e)
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  render (html, e) {
    this.renderFilters(html, e)
    this.renderProductGridContainer(html, e)
    this.renderProductCount(html)
    this.renderDynamicEls(html)

    // Ensure facet state persists after render
    if (this.facetStates) {
      for (const facetState of this.facetStates) {
        const facetEl = getElement(
          `[is='filter-facet'][data-index=${facetState.index}]`,
          { context: document },
        )

        if (!facetEl) return
        facetEl.open = facetState.open
        facetEl.setAttribute('data-fully-open', String(facetState.fullyOpen))

        if (facetState.scrollTop) {
          const contentEl = facetEl.querySelector(
            '[data-filter-facet-el="content"]',
          )
          if (contentEl) {
            contentEl.scrollTop = facetState.scrollTop
          }
        }
      }
    }

    // Preserve scroll position of drawer after render
    if (this.drawerScrollTop) {
      const drawerEl = getElement(
        'site-drawer[key="tablet-collection-filters"]',
      )
      if (drawerEl) {
        drawerEl.scrollTop = this.drawerScrollTop
      }
    }

    this.form.loading = false
    document.dispatchEvent(new CustomEvent('AFTER_COLLECTION_RENDER'))
  }

  /**
   * @param {String} html
   * @param {SubmitEvent} e
   *
   * @returns {void}
   */
  renderFilters (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')
    const detailSelector = [
      this.form.els?.facet?.selector,
      this.form.externalEls?.facet?.selector,
    ]
      .filter(Boolean)
      .join(',')

    if (!detailSelector) return

    const facetDetailElements = getElements(detailSelector, {
      context: parsedHTML,
    })
    /**
     * @param {HTMLElement} element
     *
     * @returns {Boolean}
     */
    const matchesIndex = element => {
      const facet = e ? e.target.closest(detailSelector) : null

      return facet ? element.dataset.index === facet.dataset.index : false
    }
    const facetsToRender = facetDetailElements.filter(el => !matchesIndex(el))
    const countsToRender = facetDetailElements.find(el => matchesIndex(el))

    this.form.log('renderFilters', {
      e,
      parsedHTML,
      facetDetailElements,
      facetsToRender,
      countsToRender,
    })

    facetsToRender.forEach(element => {
      const selector = detailSelector
        .split(',')
        .map(selector => `${selector}[data-index="${element.dataset.index}"]`)
        .join(',')

      const el = getElement(selector)
      el.innerHTML = element.innerHTML
    })

    this.renderActiveFacets(parsedHTML)
    this.renderAdditionalElements(parsedHTML)

    if (countsToRender) {
      this.renderSelectedFacetCount(
        countsToRender,
        e.target.closest(detailSelector),
      )
    }
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderActiveFacets (parsedHTML) {
    this.activeFacetSelectors.forEach(selector => {
      const activeFacetElement = parsedHTML.querySelector(selector)

      this.form.log('renderActiveFacets', {
        parsedHTML,
        activeFacetElement,
        selector,
      })

      if (!activeFacetElement) return

      getElements(selector).forEach(
        el => (el.innerHTML = activeFacetElement.innerHTML),
      )
    })
  }

  /**
   * @param {Document} parsedHTML
   *
   * @returns {void}
   */
  renderAdditionalElements (parsedHTML) {}

  /**
   * @param {String} html
   * @param {PointerEvent} e
   *
   * @returns {void}
   */
  renderProductGridContainer (html, e) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html')

    const newItems = document.createDocumentFragment()
    const newElements = getElements(this.form.itemSelector, {
      context: parsedHTML,
    })

    newElements.forEach(el => {
      newItems.appendChild(el)
    })

    this.form.externalEls['item-container'].element.innerHTML = ''
    this.form.externalEls['item-container'].element.append(newItems)

    /* Fix for ajax rendered blurry images in safari */
    getElements('img[srcset]:not([data-src-reset])', {
      context: this.form.externalEls['item-container'].element,
    })?.forEach(imageEl => {
      // eslint-disable-next-line no-self-assign
      imageEl.outerHTML = imageEl.outerHTML
      imageEl.setAttribute('data-src-reset', 'true')
    })

    const updatedTabletFacetsEl = getElement(
      this.form.externalEls['tablet-facets'].selector,
      { context: parsedHTML },
    )
    if (
      this.form.externalEls?.['tablet-facets']?.exists &&
      updatedTabletFacetsEl?.innerHTML
    ) {
      this.form.externalEls['tablet-facets'].element.innerHTML =
        updatedTabletFacetsEl.innerHTML
    }

    const updatedMobileFacetsEl = getElement(
      this.form.externalEls['mobile-facets'].selector,
      { context: parsedHTML },
    )
    if (
      this.form.externalEls?.['mobile-facets']?.exists &&
      updatedMobileFacetsEl?.innerHTML
    ) {
      this.form.externalEls['mobile-facets'].element.innerHTML =
        updatedMobileFacetsEl.innerHTML
    }
  }

  /**
   * @param {String} html
   *
   * @returns {void}
   */
  renderProductCount (html) {
    if (!this.form?.els?.['product-count']?.exists) return
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html')
    const selector = this.form.els['product-count'].selector
    const count = parsedHtml.querySelector(selector)?.innerHTML

    this.form.log('renderProductCount', {
      html,
      count,
      countElements: this.countElements,
    })
    if (!count) return

    this.countElements.forEach(element => {
      if (element.hasAttribute('id')) {
        element.innerHTML =
          parsedHtml.querySelector(`#${element.getAttribute('id')}`)
            ?.innerHTML || element.innerHTML
      } else {
        element.innerHTML = count
      }
    })
  }

  renderDynamicEls (html) {
    const selector = 'data-collection-filters-form-dynamic-el'
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html')

    const dynamicEls = document.querySelectorAll(`[${selector}]`)
    dynamicEls.forEach(dynamicEl => {
      const identifier = dynamicEl.dataset?.collectionFiltersFormDynamicEl
      const updatedElement = parsedHtml.querySelector(
        `[${selector}='${identifier}']`,
      )

      if (!updatedElement) return
      dynamicEl.innerHTML = updatedElement.innerHTML
    })
  }

  /**
   * @param {HTMLElement} source
   * @param {HTMLElement} target
   *
   * @returns {void}
   */
  renderSelectedFacetCount (source, target) {
    const sourceElement = getElement(
      this.form.els['facets-selected']?.selector,
      { context: source },
    )
    const targetElement = getElement(
      this.form.els['facets-selected']?.selector,
      { context: target },
    )

    if (sourceElement && targetElement) {
      targetElement.outerHTML = sourceElement.outerHTML
    }
  }

  /**
   * @returns {Element[]}
   */
  get countElements () {
    return getElements('[data-product-grid-count]')
  }

  /**
   * @returns {String[]}
   */
  get activeFacetSelectors () {
    if ('active-facets' in this.form.externalEls) {
      return [
        this.form.els['active-facets'].selector,
        this.form.externalEls['active-facets'].selector,
      ]
    }

    if (!this.form.els['active-facets']?.selector) return []
    return [this.form.els['active-facets'].selector]
  }
}

/**
 * @typedef {Object} FilterData
 *
 * @property {string} html
 * @property {string} url
 */

export class CollectionFiltersForm extends window.BAO.CustomElement({
  SuperClass: HTMLFormElement,
}) {
  static get requiredElements () {
    return ['facets', 'facet-summary']
  }

  static get requiredExternalElements () {
    return ['container', 'product-grid']
  }

  constructor () {
    super()

    /**
     * @type {Section[]}
     * @public
     */
    this.sections = [
      {
        id: 'template-shopify-collection-product-grid',
        section: this.dataset.id,
        selector: '.shopify-section',
      },
    ]

    /**
     * @type {FilterData[]}
     * @public
     */
    this.filterData = []

    /**
     * @type {{ previous: URLSearchParams, current: URLSearchParams, initial: URLSearchParams }}
     * @public
     */
    this.searchParams = {
      // eslint-disable-next-line compat/compat
      previous: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      current: new URLSearchParams(window.location.search),
      // eslint-disable-next-line compat/compat
      initial: new URLSearchParams(window.location.search),
    }

    /**
     * @type {CollectionFiltersRenderer}
     * @public
     */
    this.renderer = new CollectionFiltersRenderer(this)
  }

  setupListeners () {
    super.setupListeners()

    this.onFacetRemovalClick = this.onFacetRemovalClick.bind(this)

    this.debouncedOnSubmit = debounce(e => this.onSubmit(e), 700)

    this.listeners.add(this, 'input', this.debouncedOnSubmit.bind(this))

    this.listeners.add(window, 'popstate', this.onHistoryChange.bind(this))

    this.listeners.add(
      this.els.facets.element,
      'keyup',
      this.onKeyUp.bind(this),
    )

    this.listeners.add(this, 'click', this.handleClick.bind(this))

    this.addEventListener(
      'collection-filters:external-filter',
      this.handleExternalFilter.bind(this),
    )

    this.addEventListener(
      'collection-filters:price-bucket-filter',
      this.handleCustomPriceBucketFilter.bind(this),
    )
  }

  handleExternalFilter (e) {
    this.updateSearchParam(e.detail.filterKey, e.detail.filterValue)
  }

  handleCustomPriceBucketFilter (e) {
    if (!e.detail.filterValue) {
      return
    }

    const url = new URL(window.location)
    const searchParams = new URLSearchParams(url.search)

    e.detail.checked
      ? searchParams.append(e.detail.filterKey, e.detail.filterValue)
      : searchParams.delete(e.detail.filterKey, e.detail.filterValue)

    this.renderPage({ searchParams, updateURLHash: true })
  }

  handleClick (event) {
    if (event.target.closest(this.els?.['facet-item']?.selector)) {
      window.scrollTo(0, 0)
    }

    if (event.target.closest(this.els?.['facet-toggle']?.selector)) {
      this.toggleFacets(event)
    }
  }

  toggleFacets (event) {
    const buttonEl = event.target.closest(this?.els['facet-toggle']?.selector)
    const facetContainerEl = this.els?.['facet-container']?.element
    if (!buttonEl || !facetContainerEl) return
    const isAlreadyExpanded = buttonEl.getAttribute('aria-expanded') === 'true'
    buttonEl.setAttribute('aria-expanded', String(!isAlreadyExpanded))
    facetContainerEl.setAttribute('aria-hidden', String(isAlreadyExpanded))

    this.body.classList.toggle('lyt-FacetsHidden', isAlreadyExpanded)
    this.body.style.setProperty(
      '--InitialFacetsWidth',
      isAlreadyExpanded ? 0 : null,
    )
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  updateSearchParam (key, value) {
    if (!value) return
    const url = new URL(window.location)
    const searchParams = new URLSearchParams(url.search)

    searchParams.set(key, value)

    const newUrl = `${url.pathname}?${searchParams.toString()}`

    window.history.pushState(
      { searchParams: searchParams.toString() },
      '',
      newUrl,
    )

    this.debounced = debounce(
      e => this.renderPage({ searchParams, updateURLHash: false }),
      1200,
    )

    this.debounced()
  }

  /**
   * @param {PopStateEvent} e
   *
   * @returns {void}
   */
  onHistoryChange (e) {
    const searchParams = e.state
      ? e.state.searchParams
      : this.searchParams.previous

    if (searchParams.toString() === this.searchParams.current.toString()) {
      return
    }

    this.renderPage({ searchParams, updateURLHash: false })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onFacetRemovalClick (e) {
    e.preventDefault()

    const searchParams = e.currentTarget.href.includes('?')
      ? e.currentTarget.href.slice(e.currentTarget.href.indexOf('?') + 1)
      : ''

    this.log('onFacetRemovalClick', { searchParams })

    this.renderPage({ searchParams })
  }

  /**
   * @param {KeyboardEvent} e
   *
   * @returns {void}
   */
  onKeyUp (e) {
    if (e?.code?.toUpperCase() !== 'ESCAPE') return

    const openFacet = e.target.closest(`${this.els.facet.selector}[open]`)
    if (!openFacet) return

    const summary = getElement(`${this.els['facet-summary'].selector}`, {
      context: openFacet,
    })
    openFacet.removeAttribute('open')
    summary?.setAttribute('aria-expanded', 'false')
    summary?.focus()
  }

  repositionToElement () {
    const callback = () => {
      const el = getElement(window.location.hash)
      if (!el) return

      const { top } = el.getBoundingClientRect()
      const headerHeight = getElement(`#header`)?.offsetHeight || 0
      const offset = 20

      window.setTimeout(() => {
        window.scrollTo(0, window.scrollY + (top - headerHeight - offset))
      }, 100)
    }

    if (document.readyState === 'complete') {
      callback()
    } else {
      document.addEventListener('readystatechange', callback)
    }
  }

  /**
   * @param {InputEvent} e
   *
   * @returns {void}
   */
  onSubmit (e) {
    e.preventDefault()

    if (e.target.hasAttribute('data-disable-form-submission')) {
      return
    }

    this.saveFacetStates()

    const formData = new FormData(this)
    const fauxValue = formData.get('faux_sort_by')

    if (
      e.target?.getAttribute('name') === 'faux_sort_by' &&
      fauxValue?.length
    ) {
      const sortByEl = getElement('[name="sort_by"]', { context: this })
      if (sortByEl) {
        sortByEl.value = fauxValue
        formData.set('sort_by', fauxValue)
      }
    }

    formData.delete('faux_sort_by')

    // eslint-disable-next-line compat/compat
    const searchParams = new URLSearchParams(formData).toString()

    this.renderPage({ searchParams, e })
  }

  saveFacetStates () {
    this.renderer.facetStates = getElements('[is="filter-facet"][data-index]', {
      context: this,
    }).map(facetEl => {
      const contentEl = facetEl.querySelector(
        '[data-filter-facet-el="content"]',
      )

      return {
        index: facetEl.dataset.index,
        open: facetEl.hasAttribute('open'),
        fullyOpen: facetEl.getAttribute('data-fully-open') === 'true',
        scrollTop: contentEl ? contentEl.scrollTop : null,
      }
    })

    const drawerEl = getElement('site-drawer[key="tablet-collection-filters"]')
    if (drawerEl) {
      this.renderer.drawerScrollTop = drawerEl.scrollTop
    }
  }

  /**
   * @param {Object}
   * @param {String} searchParams
   * @param {InputEvent} [e]
   * @param {Boolean} [updateURLHash]
   *
   * @returns {void}
   */
  renderPage ({ searchParams, e, updateURLHash = true } = {}) {
    this.searchParams.previous = this.searchParams.current
    // eslint-disable-next-line compat/compat
    this.searchParams.current = new URLSearchParams(searchParams)

    this.loading = true

    this.sections.forEach(section => {
      const url = `${window.location.pathname}?view=filter&section_id=${
        section.section
      }&${this.searchParams.current.toString()}`
      /**
       * @param {FilterData} element
       *
       * @returns {Boolean}
       */
      const filterDataUrl = element => element.url === url

      this.log('renderPageSections', { section, url })

      this.filterData.some(element => filterDataUrl(element))
        ? this.renderer.fromCache(filterDataUrl, e)
        : this.renderer.fromFetch(section.section, url, e)
    })

    this.renderExternalPriceFilter()

    if (updateURLHash) this.updateURLHash(this.searchParams.current)

    document.dispatchEvent(new CustomEvent('AFTER_COLLECTION_FILTER_RENDER'))
  }

  async renderExternalPriceFilter () {
    try {
      const html = await window.BAO.sectionRenderer.fetchSingle(
        'collection-external-price-filter',
        `${window.location.pathname}?${this.searchParams.current.toString()}`,
      )

      const externalFilterContainer = document.querySelector(
        '[data-external-price-filter-el]',
      )
      if (!externalFilterContainer) {
        console.error('External filter container not found')
        return
      }

      const content = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[data-extraction-el]')
      if (!content) {
        console.error('Content for external filter not found')
        return
      }

      externalFilterContainer.innerHTML = ''
      externalFilterContainer.appendChild(content)
    } catch (error) {
      console.error('Error rendering external price filter:', error)
    }
  }

  /**
   * @param {URLSearchParams} searchParams
   *
   * @returns {void}
   */
  updateURLHash (searchParams) {
    history.pushState(
      { searchParams: searchParams.toString() },
      '',
      `${window.location.pathname}${searchParams &&
        '?'.concat(searchParams.toString())}`,
    )
  }

  get body () {
    return document.querySelector('body')
  }

  /**
   * @returns {Boolean}
   */
  get loading () {
    return this._loading
  }

  get itemSelector () {
    return '[data-collection-filters-form-external-el="item"]'
  }

  /**
   * @param {Boolean} val
   *
   * @returns {void}
   */
  set loading (val) {
    this._loading = val
  }
}

if (!customElements.get('collection-filters-form')) {
  customElements.define('collection-filters-form', CollectionFiltersForm, {
    extends: 'form',
  })
}

class FacetRemove extends window.BAO.CustomElement() {
  setupListeners () {
    super.setupListeners()

    this.listeners.add(this.querySelector('a'), 'click', e => {
      e.preventDefault()

      this.form.onFacetRemovalClick(e)
    })
  }

  get form () {
    const selector = 'collection-filters-form, [is="collection-filters-form"]'

    return this.closest(selector) || document.querySelector(selector)
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', FacetRemove)
}

class FilterFacet extends window.BAO.CustomElement({
  SuperClass: HTMLDetailsElement,
}) {
  static get requiredElements () {
    return ['summary', 'content']
  }

  constructor () {
    super()

    /**
     * @type {Animation|null}
     * @public
     */
    this.animation = null

    /**
     * @type {Boolean}
     * @public
     */
    this.isClosing = false

    /**
     * @type {Boolean}
     * @public
     */
    this.isExpanding = false
  }

  setupListeners () {
    super.setupListeners()

    this.onSummaryClick = this.onSummaryClick.bind(this)

    this.listeners.add(this, 'click', this.onSummaryClick)

    if (this.els?.search?.exists && this.els?.item?.exists) {
      this.onSearchInput = this.onSearchInput.bind(this)
      this.debouncedOnSearchInput = debounce(
        event => this.onSearchInput(event),
        250,
      )
      this.listeners.add(this, 'input', this.debouncedOnSearchInput)
    }
  }

  onSearchInput (event) {
    event.preventDefault()

    if (!event.target.closest(this.els?.search?.selector)) return

    this.els.item.elements.forEach(itemEl => {
      const isHidden = !itemEl.dataset?.value
        ?.toLowerCase()
        ?.includes(event.target.value.toLowerCase())
      itemEl.setAttribute('aria-hidden', String(isHidden))
    })
  }

  /**
   * @param {MouseEvent} e
   *
   * @returns {void}
   */
  onSummaryClick (e) {
    const summaryEl = e.target.closest(this.els?.summary.selector)
    const closeEl = e.target.closest(this.els?.close?.selector)
    if (summaryEl || closeEl) {
      if (summaryEl) {
        /* this util is required to ensure the arrow spins back before the element has fully closed */
        summaryEl.classList.toggle('util-NotActive', this.open)

        if (summaryEl && this.hasAttribute('data-filter-facet-level')) {
          if (
            this.getAttribute('data-filter-facet-level') !==
            summaryEl.getAttribute('data-filter-facet-level')
          ) {
            return
          }
        }
      }

      e.preventDefault()

      if (this.isClosing || !this.open) {
        this.start()
      } else if (this.isExpanding || this.open) {
        this.close()
      }
    }
  }

  start () {
    this.open = true

    this.setAttribute('data-fully-open', 'false')
    window.requestAnimationFrame(() => this.expand())
  }

  close () {
    this.setAttribute('data-fully-open', 'false')
    this.shrink()
  }

  shrink () {
    this.isClosing = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      this.animationConfiguration.in.keyframes,
      this.animationConfiguration.in.options,
    )

    this.animation.onfinish = () => this.onAnimationFinish(false)
    this.animation.oncancel = () => (this.isClosing = false)
  }

  expand () {
    this.isExpanding = true

    if (this.animation) {
      this.animation.cancel()
    }

    this.animation = this.content.animate(
      this.animationConfiguration.out.keyframes,
      this.animationConfiguration.out.options,
    )

    this.animation.onfinish = () => this.onAnimationFinish(true)
    this.animation.oncancel = () => (this.isExpanding = false)
  }

  /**
   * @param {Boolean} open
   *
   * @returns {void}
   */
  onAnimationFinish (open) {
    this.open = open
    this.animation = null

    this.isClosing = false
    this.isExpanding = false
    this.setAttribute('data-fully-open', String(open))
    getElement(this.els.summary.selector, { context: this })?.setAttribute(
      'aria-expanded',
      String(open),
    )
  }

  get content () {
    return window.BAO.Utils.getElement(this.els.content.selector, {
      context: this,
    })
  }

  get animationConfiguration () {
    if (this.hasAttribute('data-animate-from-right')) {
      return {
        in: {
          keyframes: {
            transform: ['translateX(0%)', 'translateX(100%)'],
          },
          options: { duration: 300, easing: 'ease' },
        },
        out: {
          keyframes: {
            transform: ['translateX(100%)', 'translateX(0%)'],
          },
          options: { duration: 300, easing: 'ease' },
        },
      }
    }

    if (this.hasAttribute('data-animate-from-bottom')) {
      return {
        in: {
          keyframes: {
            transform: ['translateY(0%)', 'translateY(100%)'],
          },
          options: { duration: 300, easing: 'ease' },
        },
        out: {
          keyframes: {
            transform: ['translateY(100%)', 'translateY(0%)'],
          },
          options: { duration: 300, easing: 'ease' },
        },
      }
    }

    return {
      in: {
        keyframes: {
          opacity: [1, 0],
          maxHeight: ['550px', '0px'],
        },
        options: { duration: 400, easing: 'ease' },
      },
      out: {
        keyframes: {
          opacity: [0, 1],
          maxHeight: ['0px', '550px'],
        },
        options: { duration: 400, easing: 'ease' },
      },
    }
  }
}

if (!customElements.get('filter-facet')) {
  customElements.define('filter-facet', FilterFacet, { extends: 'details' })
}
